import { useCallback, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid2 from "@mui/material/Unstable_Grid2";
import Divider from "@mui/material/Divider";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import Tooltip from "@mui/material/Tooltip";
import { Info } from "react-feather";
import {
  useImageUploadActions,
  useImageUploadState,
} from "src/components/useImageUploadStore";
import EnterpriseOrgSensor from "./EnterpriseOrgSensor";
import { useUser } from "src/api/useUser";
import { usePostCollectEvent } from "src/api/usePostCollectEvent";
import Autocomplete from "src/lib/Select/Autocomplete";
import DatePicker from "src/lib/DatePicker/DatePicker";
import { useCollectEvents } from "src/api/useCollectEvents";
import { ICollectEvent } from "src/types/ICollectEvent";
import { useOrthoUploader } from "src/api/useOrthoUploader";
import RequestTable from "./RequestTable";
import { useTranslation } from "react-i18next";
import { usePreventPageUnload } from "src/components/usePreventPageUnload";

export function OrthoUploader() {
  const userQ = useUser();
  const actions = useImageUploadActions();
  const {
    endDate,
    startDate,
    organization,
    formData,
    enterprise,
    overwriteCollectEvent,
    sensorType,
  } = useImageUploadState();
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [createCollectEventError, setCreateCollectEventError] = useState("");
  const collectEventMutation = usePostCollectEvent(organization?.slug);

  const uploadMutation = useOrthoUploader({
    org: organization?.slug,
  });
  const collectEventsQ = useCollectEvents(
    userQ.data?.isOrgUser
      ? userQ.data?.metadata?.tenant.org.id
      : organization?.slug ?? organization?.id,
    sensorType?.id
  );
  usePreventPageUnload(uploadMutation.isPending);

  const alwaysReqd =
    formData?.tenantIdOrSlug &&
    formData?.organizationSlug &&
    formData?.sensorTypeId &&
    file;
  const submitEnabled = overwrite
    ? overwriteCollectEvent && alwaysReqd
    : alwaysReqd;

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setCreateCollectEventError("");

      const orgSlug = formData?.organizationSlug;
      if (
        !file ||
        !enterprise?.slug ||
        !orgSlug ||
        !organization ||
        !userQ.data?.email
      ) {
        return;
      }

      const collectEventData = {
        name:
          overwrite && overwriteCollectEvent
            ? overwriteCollectEvent.name
            : formData?.collectEventName ?? "",
        sensorTypeId: formData?.sensorTypeId,
        beginOnUtc:
          overwrite && overwriteCollectEvent
            ? overwriteCollectEvent?.beginOnUtc
            : formData?.beginOnUtc ?? "",
        endOnUtc:
          overwrite && overwriteCollectEvent
            ? overwriteCollectEvent?.endOnUtc
            : formData?.endOnUtc ?? "",
      };

      const cid =
        overwrite && overwriteCollectEvent
          ? overwriteCollectEvent.id
          : await collectEventMutation
              .mutateAsync(collectEventData, {
                onError: (msg) => {
                  setCreateCollectEventError(msg);
                },
              })
              .then((res) => res.id);

      if (!cid) return;

      const body = {
        id: cid,
        userId: userQ.data.metadata.cai_user_id,
        email: userQ.data.email,
        collectEventId: cid,
        collectEventName:
          overwriteCollectEvent?.name || formData?.collectEventName,
        file,
        enterprise: enterprise.slug,
        organization: orgSlug,
        overwrite,
      };

      actions.setCollectEventName("");
      if (fileRef.current) fileRef.current.value = "";
      setFile(null);
      uploadMutation.mutate(body);
    },
    [
      actions,
      collectEventMutation,
      enterprise,
      file,
      formData?.beginOnUtc,
      formData?.collectEventName,
      formData?.endOnUtc,
      formData?.organizationSlug,
      formData?.sensorTypeId,
      organization,
      overwrite,
      overwriteCollectEvent,
      uploadMutation,
      userQ.data,
    ]
  );

  return (
    <>
      <Typography textTransform={"uppercase"} variant="h5" component="h2">
        Ortho Uploader
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography>Upload a fully processed orthomosaic .tif image</Typography>
        <Divider sx={{ mb: 2, mt: 1 }} />
        <form onSubmit={handleSubmit}>
          <EnterpriseOrgSensor />
          <Stack
            direction={"row"}
            spacing={1}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Typography>{t("common.create")}</Typography>
            <Switch
              checked={overwrite}
              onChange={(e) => {
                setOverwrite(e.target.checked);
                if (!e.target.checked) {
                  actions.setOverwriteCollectEvent(undefined);
                }
              }}
              inputProps={{ "aria-label": "Overwrite switch" }}
            />
            <Typography>{t("common.overwrite")}</Typography>
          </Stack>
          {overwrite ? (
            <Autocomplete
              label={t("common.overwriteCollectionEvent")}
              options={collectEventsQ.data}
              value={overwriteCollectEvent ?? null}
              onChange={(_e, opt) => {
                const item = opt as ICollectEvent;
                actions.setOverwriteCollectEvent(item);
              }}
            />
          ) : (
            <Grid2 container spacing={2}>
              <Grid2 sm={12} md={4}>
                <TextField
                  onChange={(e) => {
                    actions.setCollectEventName(e.target.value);
                  }}
                  value={formData?.collectEventName}
                  fullWidth
                  label={t("common.collectionEventName")}
                />
              </Grid2>
              <Grid2 sm={12} md={4}>
                <DatePicker
                  onChange={(date) => {
                    actions.setStartDate(date);
                    actions.setEndDate(date);
                  }}
                  value={startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid2>
              <Grid2 sm={12} md={4}>
                <DatePicker
                  onChange={(date) => {
                    actions.setEndDate(date);
                  }}
                  value={endDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid2>
            </Grid2>
          )}
          <Stack spacing={2} mt={2}>
            <Box sx={{ display: "flex" }}>
              <Stack>
                <InputLabel htmlFor="tiff-file">
                  TIFF image (.zip) - {t("common.seeSensorRequirements")}
                </InputLabel>
                <OutlinedInput
                  id="tiff-file"
                  type="file"
                  inputProps={{
                    ref: fileRef,
                    accept: ".zip",
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCreateCollectEventError("");
                    setFile(e.target.files ? e.target.files[0] : undefined);
                  }}
                />
              </Stack>
              <Stack
                sx={{ ml: "auto" }}
                direction={"row"}
                spacing={2}
                alignItems={"center"}
              >
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={!submitEnabled}
                >
                  {t("common.submit")}
                </Button>
              </Stack>
            </Box>
            {createCollectEventError ? (
              <Alert
                onClose={() => {
                  setCreateCollectEventError("");
                }}
                severity="error"
              >
                {createCollectEventError}
              </Alert>
            ) : null}
            {/* {uploadMutation.isError ? (
              <Alert
                onClose={() => {
                  uploadMutation.reset();
                }}
                severity="error"
              >
                {uploadMutation.error?.message ??
                  "Upload didn't succeed. Please try again."}
              </Alert>
            ) : null} */}
          </Stack>
        </form>
        <Link
          sx={{ mt: 2, display: "inline-block" }}
          rel="noreferrer"
          target="_blank"
          href="https://docs.google.com/document/d/1x0W0bioMYFHAikXYsQxgU9qd8xB1gJL2p9LC2Isua2A/edit"
          textTransform={"uppercase"}
        >
          <Tooltip title={t("common.clickHereForMoreInfo")}>
            <Stack direction={"row"} spacing={1}>
              <Typography>{t("common.sensorRequirements")}</Typography>
              <Info />
            </Stack>
          </Tooltip>
        </Link>
      </Paper>
      <Paper sx={{ p: 3, mt: 2, overflow: "auto" }}>
        <RequestTable />
      </Paper>
    </>
  );
}
